/* eslint-disable parog-web/no-glint-expect-error-directive */
import { concat, fn } from '@ember/helper';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import changesetGet from 'ember-changeset/helpers/changeset-get';
import t from 'ember-intl/helpers/t';
import add from 'ember-math-helpers/helpers/add';
import and from 'ember-truth-helpers/helpers/and';
import eq from 'ember-truth-helpers/helpers/eq';
import or from 'ember-truth-helpers/helpers/or';
import Content0 from 'parog-web/components/consultation/report-form/content';
import Content from 'parog-web/components/file-uploaded/edition-form/content';
import { type Signature as ProviderSig } from 'parog-web/components/forc/provider';
import type ConsultationReportWrittenQuestion from 'parog-web/models/consultation/consultation-report-written-question';
import type Patient from 'parog-web/models/patient';
import type SemeiaSessionService from 'parog-web/services/session';
import SelectableButtonGroup from 'ui-kit/components/selectable-button-group';
import type DocumentEditionFormComponent from '../edition-form';
import type { Period } from '../edition-form';

interface Signature {
  Args: {
    forcComponent: DocumentEditionFormComponent;
    form: ProviderSig['Blocks']['content'][0];
    validityOptionsMedication: Period[];
    patient: Patient;
  };
}

export default class DocumentEditionFormContent extends Component<Signature> {
  @service declare router: RouterService;
  @service declare session: SemeiaSessionService;

  @tracked currentQuestions: ConsultationReportWrittenQuestion[] = [];

  get changeset() {
    return this.args.forcComponent.changeset;
  }

  get providedForm() {
    return this.args.form;
  }

  onDocumentTypeChange = key => {
    if (
      this.session.patient?.healthCentre?.disableBioForPatient &&
      key == 'bio'
    ) {
      this.router.transitionTo('patient-web.protected.bio-results.edition');
    } else this.changeset.set('documentType', key);
  };

  setCurrentQuestions = questions => {
    this.currentQuestions = questions;
  };

  <template>
    {{! prettier-ignore }}
    <div class='field mb16'>
      <SelectableButtonGroup
        {{! @glint-expect-error }}
        @label='Type de document'
        @propertyName='documentType'
        @columns='four'
        @isMandatory={{true}}
        @centered={{true}}
              {{! @glint-expect-error }}
        @vertical={{@displayedFromPanel}}
              {{! @glint-expect-error }}
        @onSelectedUpdate={{fn @onDocumentTypeUpdate (changesetGet this.changeset 'documentType') }}
        data-test-type-document-group
        as |sbg|
      >
              {{! @glint-expect-error }}
        {{#each-in @items as |key value|}}
          <sbg.Item
              {{! @glint-expect-error }}
            @isReadOnly={{@isReadOnly}}
            @onChange={{fn this.onDocumentTypeChange key}}
            @selectedValue={{changesetGet this.changeset 'documentType'}}
            @value={{key}}
          >
            {{value.text}}
          </sbg.Item>
        {{/each-in}}
      </SelectableButtonGroup>
    </div>
    {{#if this.changeset.documentType}}
      {{#if (eq (changesetGet this.changeset 'documentType') 'bio')}}
        <div class='field'>
          <@form.Date
            @isMandatory={{true}}
            @labelName="Date de l'examen"
            @propertyName='occurredAt'
          />
        </div>

        <div class='field'>
          <@form.TextArea
            @labelName='Commentaire'
            @propertyName='comments'
            @rows={{4}}
          />
        </div>
      {{/if}}
      {{#if (eq (changesetGet this.changeset 'documentType') 'other')}}
        <Content @form={{this.providedForm}} />
      {{/if}}
      {{#if (eq (changesetGet this.changeset 'documentType') 'prescr')}}
        <div class='field'>
          <@form.Date
            @labelName="Date de l'ordonnance"
            @propertyName='occurredAt'
            @isMandatory={{true}}
          />
        </div>
        <div class='field'>
          <@form.Dropdown
            @propertyName='fileSubType'
            @labelName="Type de l'ordonnance"
            @placeholder='Sélectionnez ou tapez votre texte'
            @isMandatory={{true}}
            data-test-subtype
            as |dropdown|
          >
            <dropdown.Item
              @key='bio-exam-prescribed-journal-event'
              @value='Biologie'
            />
            <dropdown.Item
              @key='medication-prescribed-journal-event'
              @value='Médicament'
            />
            <dropdown.Item
              @key='medical-imaging-prescribed-journal-event'
              @value='Examen ou Imagerie'
            />
            <dropdown.Item
              @key='paramedical-care-prescribed-journal-event'
              @value='Soins paramédicaux'
            />

          </@form.Dropdown>
        </div>
        {{#if
          (or
            (eq
              (changesetGet this.changeset 'fileSubType')
              'bio-exam-prescribed-journal-event'
            )
            (eq
              (changesetGet this.changeset 'fileSubType')
              'medication-prescribed-journal-event'
            )
          )
        }}

          <div class='field'>
            <@form.Dropdown
              @labelName='Durée de validité'
              @placeholder='Durée de validité'
              @propertyName='validityPeriod'
              @isMandatory={{false}}
              data-test-validity-period
              as |dropdown|
            >
              {{#if
                (eq
                  (changesetGet this.changeset 'fileSubType')
                  'medication-prescribed-journal-event'
                )
              }}
                {{#each @validityOptionsMedication as |period|}}
                  <dropdown.Item
                    @key={{concat period.duration '-' period.unit}}
                    @value={{concat
                      period.duration
                      ' '
                      (t (concat 'prescriptions.validity-period.' period.unit))
                    }}
                  />
                {{/each}}
              {{else if
                (eq
                  (changesetGet this.changeset 'fileSubType')
                  'bio-exam-prescribed-journal-event'
                )
              }}
                {{! @glint-expect-error }}
                {{#each @validityOptionsBioExam as |validityOption index|}}
                  <dropdown.Item
                    @key={{add index 1}}
                    @value={{validityOption}}
                  />
                {{/each}}
              {{/if}}
            </@form.Dropdown>
          </div>
        {{/if}}

        <div class='field'>
          <@form.TextArea
            @labelName='Commentaire'
            @propertyName='body'
            @rows={{4}}
          />
        </div>
      {{/if}}
      {{#if (eq (changesetGet this.changeset 'documentType') 'cr')}}
        <Content0
          @forcComponent={{@forcComponent}}
          @form={{this.providedForm}}
          @patient={{@patient}}
          @setCurrentQuestions={{this.setCurrentQuestions}}
          @questions={{this.currentQuestions}}
        />
      {{/if}}
      <div class='field mt32 mb32'>
        <@form.FileUpload
          @propertyName='fileAttachmentsAttributes'
          {{! @glint-expect-error }}
          @isMandatory={{true}}
        />
      </div>
      {{#if (and this.session.medic @patient.authorizesAppUsage)}}
        {{#if (eq (changesetGet this.changeset 'documentType') 'prescr')}}
          <@form.SendMessage
            @willSendMessage={{false}}
            @defaultChatMessage='Bonjour,
Je viens de vous adresser un nouveau document. Il est joint à ce message.
Bonne journée.'
          />
        {{else}}
          <@form.SendMessage
            @willSendMessage={{@patient.isEnrolled}}
            @defaultChatMessage='Bonjour,
Je viens de vous adresser un nouveau document. Il est joint à ce message.
Bonne journée.'
          />
        {{/if}}
      {{/if}}
    {{/if}}
  </template>
}
